import React, { useState, useRef, useMemo } from "react";
import { CAPABILITIES_TABS } from "../data/content";
import useInView from "../hooks/useInView";
import { Tag } from "./ui-primitives";

export default function HeroCapabilitiesTabs() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = CAPABILITIES_TABS[activeIdx];
  const [tickerRef, tickerInView] = useInView();

  const tablistRef = useRef(null);
  const pillRefs = useRef([]);
  const snapTimeoutRef = useRef(null);

  // Scroll the tapped pill into the visible left side of the scroller.
  // Same proven approach as HeroStatsTabs — uses getBoundingClientRect so it
  // works regardless of which direction the user is moving (forward or reverse).
  const scrollActiveIntoView = (idx) => {
    const container = tablistRef.current;
    const el = pillRefs.current[idx];
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const tabPos = eRect.left - cRect.left + container.scrollLeft;
    // Offset so the previous pill peeks on the left.
    const PEEK = 32;
    const target = Math.max(
      0,
      Math.min(tabPos - PEEK, container.scrollWidth - container.clientWidth)
    );
    try {
      container.scrollTo({ left: target, behavior: "smooth" });
    } catch {
      container.scrollLeft = target;
    }
    if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    snapTimeoutRef.current = setTimeout(() => {
      if (tablistRef.current) tablistRef.current.scrollLeft = target;
    }, 480);
  };

  const selectTab = (i) => {
    setActiveIdx(i);
    requestAnimationFrame(() => scrollActiveIntoView(i));
  };

  // Marquee items rebuild only when the active tab changes — not on every
  // parent re-render. Without memoisation the flatMap creates ~50 elements
  // per render even when only an unrelated state value changes.
  const tickerItems = useMemo(() => {
    const copies = active.items.length >= 8 ? 2 : 4;
    return Array.from({ length: copies }).flatMap((_, copyIdx) =>
      active.items.map((cap, i) => (
        <div
          key={`${copyIdx}-${i}`}
          className="flex items-center gap-5 whitespace-nowrap"
        >
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(1.25rem, 2.4vw, 1.75rem)",
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "rgba(249,249,248,0.92)",
            }}
          >
            {cap}
          </span>
          <span
            aria-hidden
            style={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              background: "var(--color-tertiary)",
              display: "inline-block",
            }}
          />
        </div>
      ))
    );
  }, [active]);

  return (
    <div data-testid="hero-capabilities-tabs">
      {/* Header — shared <Tag> component for visual consistency with every
          other section heading on the page. */}
      <div className="di-container mb-4">
        <Tag light>What We Do</Tag>
      </div>

      {/* Tab pills — horizontal scroll on mobile (no stacking), wrapped row on desktop */}
      <div className="di-container mb-5" style={{ position: "relative" }}>
        <div
          ref={tablistRef}
          data-testid="capabilities-tablist"
          className="hide-scrollbar flex items-center gap-2 overflow-x-auto md:flex-wrap"
          style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}
        >
          {CAPABILITIES_TABS.map((t, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={t.id}
                ref={(el) => (pillRefs.current[i] = el)}
                type="button"
                data-testid={`capabilities-tab-${t.id}`}
                onClick={() => selectTab(i)}
                aria-pressed={isActive}
                className="label-md"
                style={{
                  padding: "6px 14px",
                  borderRadius: "var(--radius-pill)",
                  cursor: "pointer",
                  background: isActive ? "var(--color-tertiary)" : "transparent",
                  color: isActive
                    ? "var(--color-on-tertiary)"
                    : "rgba(249,249,248,0.85)",
                  border: isActive
                    ? "1px solid var(--color-tertiary)"
                    : "1px solid rgba(249,249,248,0.18)",
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                  transition:
                    "background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {/* Right-edge fade — only on mobile where overflow can occur */}
        <div
          aria-hidden
          className="md:hidden"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: 36,
            pointerEvents: "none",
            background:
              "linear-gradient(to right, rgba(4,13,35,0) 0%, rgba(4,13,35,0.95) 100%)",
          }}
        />
      </div>

      {/* Ticker — re-mounts on tab change so the marquee restarts cleanly.
          Pauses when scrolled out of view to save CPU + reduce competing
          motion when multiple tickers exist on the page. */}
      <div
        ref={tickerRef}
        key={active.id}
        data-testid={`capabilities-ticker-${active.id}`}
        style={{
          overflow: "hidden",
          paddingTop: 4,
          paddingBottom: 4,
          animation: "fadeInTicker 0.45s ease both",
        }}
      >
        <div
          className="marquee-track flex items-center"
          style={{
            width: "max-content",
            gap: 40,
            // Tie marquee duration to total character count (a rough proxy
            // for rendered track width) so visual pixel-velocity stays
            // constant across tabs. The earlier `items.length * 3.2s`
            // formula treated all items as equal width — so the Placement
            // tab (with long labels like "Government Advertising" and
            // "Print & Publications") felt visibly slower than Creative.
            animationDuration: `${
              active.items.reduce((s, it) => s + it.length, 0) * 0.18
            }s`,
            animationPlayState: tickerInView ? "running" : "paused",
          }}
        >
          {/* Render enough copies of the items so the marquee track is at least
              2× the viewport width on wide screens. Without this, tabs with few
              items (e.g. 4-item CREATIVE) leave a visible gap on the right edge
              after the marquee scrolls — because translateX(-50%) of a narrow
              track doesn't reach the next loop. We use even counts so -50%
              always lands on an item boundary. Items are memoised above. */}
          {tickerItems}
        </div>
      </div>

      {/* Mobile dot indicator — mirrors BY THE NUMBERS pattern. md+ shows all
          pills in a single visible row so dots would be redundant. */}
      <div className="di-container md:hidden flex items-center justify-center gap-2 pt-5">
        {CAPABILITIES_TABS.map((t, i) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(i)}
            data-testid={`capabilities-dot-${i}`}
            aria-label={`Show ${t.label}`}
            style={{
              width: i === activeIdx ? 22 : 6,
              height: 6,
              borderRadius: 3,
              background:
                i === activeIdx
                  ? "var(--color-tertiary)"
                  : "rgba(249,249,248,0.22)",
              border: "none",
              cursor: "pointer",
              transition: "width 0.3s ease, background-color 0.3s ease",
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes fadeInTicker { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
